import PtApiClient from 'plant-api-client';
import ContentSelectorDialog from '../components/ContentSelectorDialog';
import PServiceContent from 'plant-common/src/services/PServiceContent';
import PServiceXAPI from 'plant-common/src/services/PServiceXAPI';
import { libraryApi } from '@/api/index.js';
import store from '@/store';
import ObjectivePreview from '@/components/lessonPlan/components/slides/objectives/ObjectivePreview.vue';
import componentModal from '@/utils/componentModal.js';
import { getContentId, getMajorVersion, getMinorVersion, getType } from '@/utils/dbTool';
import { STORE_LIBRARY_NAME } from '@/store_constants/library';
import * as CommonApps from '@/pages/commonApps';

/**
 * Implements getContentByCustomId service
 * @param {import('plant-common/src/services/PServiceContent').GetContentByCustomIdOptions} options
 * @returns {Promise<Object>} The content found
 */
const getContentByCustomIdDriver = function ({ custom_id, library_id }) {
	if (library_id) {
		return libraryApi.searchContentByCustomId(library_id, { custom_id }).then(r => {
			r.data.header.library_id = library_id;
			return r.data;
		});
	}
	return PtApiClient.getContentByCustomId(custom_id);
};

/**
 * Implements findContentByCustomId service
 * @param {import('plant-common/src/services/PServiceContent').FindContentByCustomIdOptions} options
 * @returns {Promise<Array<Object>>} An array with the content found
 */
const findContentByCustomIdDriver = function ({ custom_id, library_id }) {
	return getContentByCustomIdDriver({ custom_id, library_id }).then(content => [content]);
};

/**
 * Implements searchContent service
 * @param {import('plant-common/src/services/PServiceContent').SearchContentOptions} options
 * @returns {Promise<{docs: Array<Object>}>} Content list
 */
const searchContentDriver = function ({ type, text, hotspot, library_id }) {
	if (library_id) {
		return libraryApi.searchContentByTypeAndText(library_id, type, text, hotspot).then(r => r.data);
	}
	return PtApiClient.getContentWithTypeOrText(type, text, hotspot);
};

function openApp(app, locator) {
	const href = CommonApps.getAppUrl(app, locator.libraryId, locator.contentId, locator.revision);
	window.open(href, '_blank');
}

function isObjectId(string) {
	return string.match(/^[0-9a-fA-F]{24}$/);
}

function isPath(string) {
	const regexCrossLibrary = new RegExp('plant(|-ref|-xref|-link)://');
	const isAbsolutePath = string.startsWith('plant://');

	return regexCrossLibrary.test(string) || isAbsolutePath;
}

function setUrlHash(tag) {
	const invalidTags = ['null', 'undefined', ''];

	return tag && !invalidTags.includes(tag) ? `#${tag}` : '';
}

function getURLFromString(string, library_id, tag) {
	if (isObjectId(string)) {
		return `/content/${library_id}/${string}/latest${setUrlHash(tag)}`;
	}

	if (isPath(string)) {
		const [, , library_id, content, content_id, major_revision, minor_revision] = string.split('/');
		return `/content/${library_id}/${content_id}/${major_revision}/${minor_revision}${setUrlHash(tag)}`;
	}

	const [, , content_id, major_revision, minor_revision] = string.split('/');
	return `/content/${library_id}/${content_id}/${major_revision}/${minor_revision}${setUrlHash(tag)}`;
}

function getURLFromHeader(header, libraryId, tag) {
	const { major_revision, minor_revision } = header;
	const library_id = header.library_id ? header.library_id : libraryId;
	const content_id = header.metadata.content_id;

	return `/content/${library_id}/${content_id}/${major_revision}/${minor_revision}${setUrlHash(tag)}`;
}

function getURLFromObject(object, library_id, tag) {
	if (object.$origin) {
		return getURLFromString(object.$origin, library_id, tag);
	}

	return getURLFromHeader(object.header, library_id, tag);
}

function getLocatorFromHeader(header, libraryId) {
	const { major_revision, minor_revision } = header;
	const library_id = header.library_id ? header.library_id : libraryId;
	const content_id = header.metadata.content_id;

	return {
		libraryId: library_id,
		contentId: content_id,
		revision: `${major_revision}.${minor_revision}`
	};
}

function openInNewTabFromURL(url, library_id, urlParams) {
	window.open(getURLFromString(url, library_id, urlParams));
}

function openInNewTabFromRevision(revision, library_id, urlParams) {
	const libraryId = revision.header.metadata.library_id || library_id;
	const urlToOpen = getURLFromObject(revision, libraryId, urlParams);
	const contentType = getType(revision);

	if (CommonApps.isAppAvailableAsNewApp(contentType)) {
		const locator = getLocatorFromHeader(revision.header, library_id);
		openApp(CommonApps.getAppName(contentType), locator);
	} else {
		window.open(urlToOpen);
	}
}

const openContentInNewTabDriver = function (urlOrRevision, urlParams) {
	const library_id = store.state[STORE_LIBRARY_NAME].settings._id;

	if (typeof urlOrRevision === 'string') {
		openInNewTabFromURL(urlOrRevision, library_id, urlParams);
		return;
	}

	openInNewTabFromRevision(urlOrRevision, library_id, urlParams);
};

const openContentInDialogDriver = async function (urlOrRevision) {
	let content;

	if (typeof urlOrRevision === 'string') {
		if (isObjectId(urlOrRevision)) {
			content = await PtApiClient.getContentById(urlOrRevision);
		} else {
			const [, , content_id, major_revision, minor_revision] = urlOrRevision.split('/');

			content = await PtApiClient.getRevisionByContentId(content_id, major_revision, minor_revision);
		}
	} else {
		content = urlOrRevision;
	}

	await componentModal(ObjectivePreview, { content });
};

const openContentInCurrentTabDriver = function (revisionLocator) {
	const defaultLibraryId = store.state[STORE_LIBRARY_NAME].settings._id;
	const library_id = revisionLocator.library_id || defaultLibraryId;

	window.open(`/content/${library_id}/${revisionLocator.itemId}/latest?from=${revisionLocator.from}`, '_self');
};

const getLatestDriver = async function (arrayCustomIds) {
	return await PtApiClient.getLatestVersions(arrayCustomIds);
};

const selectContentDriver = function (filters) {
	return ContentSelectorDialog(filters);
};

const submitStatementDriver = function (req) {
	// Call PtApi endpoint
	return new Promise(function (resolve, reject) {
		resolve(req);
	});
};

const uploadFileServiceDriver = async function (file, contentId) {
	const contentIdentification = contentId || store.state.content.itemRevision.header.metadata.content_id;
	const { filename, url } = await PtApiClient.uploadDraftFile(contentIdentification, file);
	return { filename, url };
};

const getDependentsDriver = async function (content) {
	return await PtApiClient.getContentDependents(
		getContentId(content),
		getMajorVersion(content),
		getMinorVersion(content)
	);
};

const getContentFromRefDriver = async function (ref) {
	if (ref) {
		const regexAbsolute = new RegExp('plant://');
		const regexRelative = new RegExp('plant:/content/');
		if (regexRelative.test(ref)) {
			var [, , content_id, major_revision, minor_revision] = ref.split('/');
			if (content_id && major_revision && minor_revision) {
				const content = await PtApiClient.getRevisionByContentId(content_id, major_revision, minor_revision);
				return content;
			} else {
				return new Error('Malparsed $ref: id, major_version, minor_version not set');
			}
		} else if (regexAbsolute.test(ref)) {
			var [protocol, empty, library_id, content, content_id, major_revision, minor_revision] = ref.split('/');
			if (library_id && content_id && major_revision && minor_revision) {
				const { data } = await libraryApi.getContentByRevision(library_id, content_id, major_revision, minor_revision);
				return data;
			} else {
				return new Error('Malparsed $ref: library_id, id, major_version, minor_version not set');
			}
		}
	} else {
		return new Error('Invalid $ref');
	}
};

PServiceContent.registerDriver({
	getContentByCustomIdDriver,
	findContentByCustomIdDriver,
	searchContentDriver,
	selectContentDriver,
	getLatestDriver,
	openContentInNewTabDriver,
	openContentInDialogDriver,
	openContentInCurrentTabDriver,
	uploadFileServiceDriver,
	getDependentsDriver,
	getContentFromRefDriver
});
PServiceXAPI.registerDriver({ submitStatementDriver });
